// Offline Play — persistent sync queue + board-metadata catalog.
//
// When the player finishes a game with no connectivity, the result is pushed
// onto a durable queue (Capacitor Preferences on device, localStorage on web).
// The queue is flushed to POST /api/game/sync whenever connectivity returns
// (app resume / `online` event / Dashboard mount). The server applies the
// SAME reward logic as the online routes and de-dups strictly by `op_id`.

import { Capacitor } from "@capacitor/core";
import { Preferences } from "@capacitor/preferences";
import { api } from "./api";

const QUEUE_KEY = "fj_offline_queue";
const CATALOG_KEY = "fj_board_catalog"; // { "world/level" | "mode:daily": {...} }

function prefsAvailable() {
  try {
    return Capacitor.isNativePlatform() && Capacitor.isPluginAvailable("Preferences");
  } catch (_) {
    return false;
  }
}

async function readRaw(key) {
  if (prefsAvailable()) {
    try {
      const { value } = await Preferences.get({ key });
      if (value != null) return value;
    } catch (_) { /* fall through */ }
  }
  try { return localStorage.getItem(key); } catch (_) { return null; }
}

async function writeRaw(key, value) {
  if (prefsAvailable()) {
    try { await Preferences.set({ key, value }); } catch (_) { /* fall through */ }
  }
  try { localStorage.setItem(key, value); } catch (_) { /* ignore */ }
}

async function readJSON(key, fallback) {
  const raw = await readRaw(key);
  if (!raw) return fallback;
  try { return JSON.parse(raw); } catch (_) { return fallback; }
}

function newOpId() {
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return `op_${crypto.randomUUID()}`;
  } catch (_) { /* ignore */ }
  return `op_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;
}

function emitQueueChanged() {
  try { window.dispatchEvent(new Event("fj-queue-changed")); } catch (_) { /* ignore */ }
}

export function isOnline() {
  try { return navigator.onLine !== false; } catch (_) { return true; }
}

// ---------------- Board-metadata catalog -----------------------------------
// Lets the client resolve which LAYOUT (and par time) to generate offline.

export async function cacheJourneyCatalog(worlds) {
  if (!Array.isArray(worlds)) return;
  const catalog = await readJSON(CATALOG_KEY, {});
  for (const w of worlds) {
    for (const lvl of w.levels || []) {
      catalog[`${w.world_id}/${lvl.level_id}`] = {
        layout: lvl.layout,
        target_time_sec: lvl.target_time_sec,
        reward_coins: lvl.reward_coins,
      };
    }
  }
  await writeRaw(CATALOG_KEY, JSON.stringify(catalog));
}

export async function cacheModeBoard(mode, layout, targetTimeSec) {
  if (!layout) return;
  const catalog = await readJSON(CATALOG_KEY, {});
  catalog[`mode:${mode}`] = { layout, target_time_sec: targetTimeSec };
  await writeRaw(CATALOG_KEY, JSON.stringify(catalog));
}

// Resolve a board layout + par time for offline play. Falls back to a sane
// default so the player always gets a playable board even with a cold cache.
export async function resolveOfflineBoard({ mode, worldId, levelId, quick }) {
  const catalog = await readJSON(CATALOG_KEY, {});
  if (mode === "daily" || mode === "weekly" || mode === "endurance") {
    const c = catalog[`mode:${mode}`];
    if (c) return c;
  }
  if (!quick && worldId && levelId) {
    const c = catalog[`${worldId}/${levelId}`];
    if (c) return c;
  }
  // Cold-cache fallbacks per mode.
  const defaults = {
    quick: { layout: "diamond", target_time_sec: 120 },
    daily: { layout: "hexagon", target_time_sec: 240 },
    weekly: { layout: "gym_temple", target_time_sec: 300 },
    endurance: { layout: "cross", target_time_sec: 120 },
    journey: { layout: "diamond", target_time_sec: 180 },
  };
  return defaults[mode] || defaults.journey;
}

// ---------------- Sync queue ------------------------------------------------

export async function getQueue() {
  const q = await readJSON(QUEUE_KEY, []);
  return Array.isArray(q) ? q : [];
}

export async function queueSize() {
  return (await getQueue()).length;
}

// Enqueue a finished offline game. Returns the op that was stored.
export async function enqueueOp({ mode = "journey", worldId, levelId, won, timeTakenSec, tilesMatched, boostersUsed }) {
  const op = {
    op_id: newOpId(),
    mode,
    world_id: worldId || "couch_to_active",
    level_id: levelId || "ca_1",
    won: !!won,
    time_taken_sec: Math.max(0, Math.round(timeTakenSec || 0)),
    tiles_matched: Math.max(0, Math.round(tilesMatched || 0)),
    boosters_used: boostersUsed || {},
    created_at: new Date().toISOString(),
  };
  const q = await getQueue();
  q.push(op);
  await writeRaw(QUEUE_KEY, JSON.stringify(q));
  emitQueueChanged();
  return op;
}

let flushing = false;

// Flush queued ops to the backend. Returns { ok, synced, profile } where
// `profile` is the merged server profile (or null if nothing was synced).
export async function flushQueue() {
  if (flushing) return { ok: false, synced: 0, profile: null };
  if (!isOnline()) return { ok: false, synced: 0, profile: null };
  const q = await getQueue();
  if (q.length === 0) return { ok: true, synced: 0, profile: null };
  flushing = true;
  try {
    const { data } = await api.post("/game/sync", { ops: q });
    // Drop ops the server processed, skipped (dup) OR errored (not retryable)
    // so a malformed op can never loop in the queue forever.
    const done = new Set([
      ...(data.processed || []),
      ...(data.skipped || []),
      ...(data.errors || []),
    ]);
    const remaining = q.filter((op) => !done.has(op.op_id));
    await writeRaw(QUEUE_KEY, JSON.stringify(remaining));
    emitQueueChanged();
    return {
      ok: true,
      synced: (data.processed || []).length,
      profile: data.profile || null,
      coinsSynced: data.coins_synced || 0,
      unlockedWorld: data.unlocked_world || null,
    };
  } catch (_) {
    // Still offline / server unreachable — keep the queue for the next try.
    return { ok: false, synced: 0, profile: null, coinsSynced: 0, unlockedWorld: null };
  } finally {
    flushing = false;
  }
}
